import { computed } from "@angular/core";
import { tapResponse } from "@ngrx/operators";
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { ProductsCategoriesEndpoint, VisitOrdersSearchEndpoint } from "@pims-endpoints";
import {
	getCachedValue,
	LocalStorageKeys,
	removeCachedValue,
	setCachedValue
} from "@shared-utils";
import {
	Categories,
	VisitOrderConstants,
	Visits
} from "@types-lib";
import { distinctUntilChanged, pipe, switchMap, tap, throttleTime } from "rxjs";
import { VisitOrdersStoreState } from "./visit-order.model";

const defaultSearch = {
	search: '',
	category_code: 'All',
	state: 'All states',
	start_time: null,
	end_time: null,
	is_slv: 'all',
	partner: 'all',
	include_all_blocked: false,
	timezone: 'Europe/London',
	limit: 100,
	offset: 0,
	sort: 'default',
	order: 'desc',
	total: 0,
	saveFilter: true,
	saveOnChange: true
}

const savePageKey = LocalStorageKeys.SAVE_VISIT_PAGE_KEY
const searchFilterKey = LocalStorageKeys.SAVE_VISIT_FILTER_KEY

const savedSearch = getCachedValue(searchFilterKey)
const savedPage = getCachedValue(savePageKey)

const page = savedPage?.saveOnChange ? savedPage : {
	limit: 100,
	offset: 0,
	total: 9999
};

const searchQuery = savedSearch?.saveFilter ? {...savedSearch,sort: 'default',order: 'desc',} : defaultSearch

const initialState: VisitOrdersStoreState = {
	orders: {
		data: [],
		pagination: page,
		isLoading: false,
	},
	searchQuery: {...searchQuery, ...page},
	visitOrderAdminStatuses: {
		adminStatuses: {...VisitOrderConstants.VisitAdminStatuses},
		selectedAdminStatuses: [],
	},
	visitOrderCategories: {
		categories: [],
		selectedVisitOrderCategories: [],
		isLoading: false,
	}
}

function flattenCategories(categories: Categories.Category[]) {
	const {category_code} = searchQuery
	return categories.flatMap((category) =>
		category.children && category.children.length
			? category.children.map((childCategory) => ({
				label: `${childCategory.code} - ${childCategory.display_name}`,
				...category,
				code: childCategory.code,
				selected: Array.isArray(category_code) ? category_code.includes(childCategory.code) : false,
			}))
			: {
				label: `${category.code} - ${category.display_name}`,
				...category,
				selected: Array.isArray(category_code) ? category_code.includes(category.code) : false,
			}
	);
}

export const VisitOrderStore = signalStore(
	withState(initialState),
	withComputed(({orders, visitOrderCategories, visitOrderAdminStatuses}) => ({
		checkedForRejected: computed(() => ({...orders(), data: orders().data.map((order) => ({
				...order,
				has_rejected: Object.values(order.user.visit_counts).some(service => service.rejected)
			}))})),
		currentMinView: computed(() => (orders.pagination().offset + 1)),
		currentMaxView: computed(() => (orders.pagination().offset + orders.pagination().limit)),
		flattenVisitCategoryOrderSelect: computed(() => flattenCategories(visitOrderCategories().categories)),
		adminStatusesSelect: computed(() => {
			const {admin_status} = searchQuery
			return Object.values(visitOrderAdminStatuses().adminStatuses)
				.map(adminStatus => ({
					label: adminStatus.StringValue,
					...adminStatus,
					selected: Array.isArray(admin_status) ? admin_status.includes(adminStatus.NumericValue) : false
				}))
		})
	})),
	withMethods((
		store,
		visitOrdersSearchEndpoint = new VisitOrdersSearchEndpoint(),
		productsCategoriesEndpoint = new ProductsCategoriesEndpoint()
		) => ({
		updateSearchQuery(query: Visits.OrderQuery) {
			const {limit, total, offset, saveOnChange, ...restOfQuery} = query
			setCachedValue(searchFilterKey, restOfQuery)
			if (query.saveFilter) {
				setCachedValue(searchFilterKey, restOfQuery)
			} else if (!query.saveFilter) {
				removeCachedValue(searchFilterKey)
			}
			patchState(store, {searchQuery: query});
		},
		loadVisitOrderCategories: rxMethod<void>(
			pipe(
				tap(() => patchState(store, {visitOrderCategories: {...store.visitOrderCategories(), isLoading: true}})),
				switchMap(() => {
					return productsCategoriesEndpoint.get()
						.pipe(
							tapResponse({
								next: (categories) => patchState(store, {visitOrderCategories:{...store.visitOrderCategories(),categories}}),
								error: console.error,
								finalize: () => patchState(store, {visitOrderCategories: {...store.visitOrderCategories(), isLoading: false}}),
							})
						)
				})
			)
		),
		fetchVisitOrdersByQuery: rxMethod(
			pipe(
				throttleTime(300),
				distinctUntilChanged(),
				tap(() => patchState(store, {orders: {...store.orders(), isLoading: true}})),
				switchMap((searchQuery) => {
					return visitOrdersSearchEndpoint.get(searchQuery as Visits.OrderQuery)
						.pipe(
							tapResponse({
								next: (orders) => patchState(store, {orders}),
								error: console.error,
								finalize: () => patchState(store, {orders: {...store.orders(), isLoading: false}}),
							})
						)
				})
			)
		)
	})),
	withHooks({
		onInit(store) {
			store.loadVisitOrderCategories()
			store.fetchVisitOrdersByQuery(store.searchQuery)
		},
		onDestroy(store) {
			store.fetchVisitOrdersByQuery.destroy()
			store.loadVisitOrderCategories().destroy()
		}
	})
)
